import { Injectable } from '@angular/core';
import { TUserLoggedInInfo } from '../unified-login/unified-login.types';
import {
  Capacitor,
  CapacitorCookies,
  CapacitorHttp,
  HttpOptions,
  HttpParams,
  HttpResponse,
  SetCookieOptions,
} from '@capacitor/core';
import * as moment from 'moment';
import { UnifiedLoginEnvService } from '../unified-login/unified-login-env.service';
import { BuildUtils } from '@monorepo/utils';
import { Device, DeviceId, DeviceInfo } from '@capacitor/device';
import { Observable, from } from 'rxjs';
import { GetCookieOptions } from '@capacitor/core/types/core-plugins';

type TSafeHTTPResponse<T> = {
  data: T;
} & Pick<HttpResponse, 'headers' | 'status' | 'url'>;

type TDateResponseTypes = 'COMPRESSED' | 'UNCOMPRESSED';

type TCookiesMap = Pick<TUserLoggedInInfo, 'username' | 'securityKey' | 'factoryId'>;
type TCookieKeys = keyof TCookiesMap;
type TDeviceCache = {
  deviceInfo: DeviceInfo;
  deviceId: DeviceId;
};
export class HttpResponseException extends Error {
  httpResponse: HttpResponse;
  constructor(httpRes: HttpResponse) {
    super(`HttpResponseException`);
    this.httpResponse = httpRes;
  }
}

@Injectable({
  providedIn: 'root',
})
export class CapacitorHttpService {
  deviceCache: TDeviceCache | undefined;
  constructor(private unifiedLoginEnv: UnifiedLoginEnvService) {}

  async getDeviceIdAndDeviceInfo() {
    const deviceId = await Device.getId();
    const deviceInfo = await Device.getInfo();
    return {
      deviceId,
      deviceInfo,
    };
  }

  async init() {
    this.deviceCache = await this.getDeviceIdAndDeviceInfo();
  }

  async getDeviceInfo() {
    if (this.deviceCache) return this.deviceCache;
    return await this.getDeviceIdAndDeviceInfo();
  }

  getDeviceHeader = async () => {
    const { deviceId, deviceInfo } = await this.getDeviceInfo();
    const { model, platform, operatingSystem, osVersion, manufacturer, webViewVersion } = deviceInfo;
    const { identifier } = deviceId;
    return {
      'X-Client-Details': JSON.stringify({
        appVersion: BuildUtils.APP_VERSION,
        deviceId: identifier,
        model,
        platform,
        operatingSystem,
        osVersion,
        manufacturer,
        webViewVersion,
      }),
    } as const;
  };

  async postPromise<Res, Compresed extends TDateResponseTypes = 'UNCOMPRESSED', Req = unknown>(
    url: HttpOptions['url'],
    postData: Req,
    httpOptions: HttpOptions['headers'] = {},
  ): Promise<Compresed extends 'UNCOMPRESSED' ? Res : string> {
    const deviceHeaders = await this.getDeviceHeader();
    const options: HttpOptions = {
      method: 'POST',
      url,
      headers: { 'Content-Type': 'application/json', ...httpOptions, ...deviceHeaders },
      data: postData,
      responseType: 'json',
    };
    return CapacitorHttp.request(options)
      .then((v) => {
        switch (v.status) {
          case 200: {
            return v.data;
          }
          default: {
            throw new HttpResponseException(v);
          }
        }
      })
      .catch((error) => {
        if (!error.httpResponse) throw new HttpResponseException({ status: 0, data: [], headers: {}, url: '' });
        else throw new HttpResponseException(error.httpResponse);
      });
  }

  post<Res, Compresed extends TDateResponseTypes = 'UNCOMPRESSED', Req = unknown>(
    url: HttpOptions['url'],
    postData: Req,
    httpOptions: HttpOptions['headers'] = {},
  ): Observable<Compresed extends 'UNCOMPRESSED' ? Res : string> {
    return from(this.postPromise<Res, Compresed, Req>(url, postData, httpOptions));
  }
  async get<Res, Compresed extends TDateResponseTypes = 'UNCOMPRESSED'>(
    url: HttpOptions['url'],
    params: HttpParams | null,
    httpOptions: HttpOptions['headers'] = {},
  ): Promise<TSafeHTTPResponse<Compresed extends 'UNCOMPRESSED' ? Res : string>> {
    const commonHeaders = await this.getDeviceHeader();

    const options: HttpOptions = {
      url,
      headers: { ...httpOptions, ...commonHeaders },
      ...(params && { params: { ...params } }),
    };
    return CapacitorHttp.get(options);
  }

  async setCookie(cookie: SetCookieOptions) {
    await CapacitorCookies.setCookie(cookie);
    if (Capacitor.isNativePlatform()) {
      await CapacitorCookies.setCookie({
        ...cookie,
        url: this.unifiedLoginEnv.HOST_DOMAIN_WITH_PROTOCOL,
      });
    }
  }

  async getCookie(key: string, options?: GetCookieOptions) {
    const cookies = await CapacitorCookies.getCookies(options);
    return cookies[key];
  }

  setUserCookie(cookieMap: TCookiesMap, keyExpiryInMillis: number) {
    (Object.keys(cookieMap) as TCookieKeys[]).forEach(async (v) => {
      const expirtyTime = moment().add(keyExpiryInMillis, 'ms');
      const isNotNative = !Capacitor.isNativePlatform();
      // NOTE: CapacitorCookies.setCookie is not setting cookies correctly
      // [anubhav] Check why setCookie is not working on web
      if (expirtyTime) {
        if (isNotNative) {
          document.cookie = `${v}=${cookieMap[v]};expires=${expirtyTime.toString()};path=/`;
        } else {
          console.log('[Capacitor] Setting cookie for ', this.unifiedLoginEnv.HOST_DOMAIN_WITH_PROTOCOL);
          await CapacitorCookies.setCookie({
            url: this.unifiedLoginEnv.HOST_DOMAIN_WITH_PROTOCOL,
            key: v,
            value: cookieMap[v],
            expires: expirtyTime.toString(),
          });
        }
      }
    });
  }

  async clearAllCookies() {
    CapacitorCookies.clearAllCookies();
    document.cookie = '';
  }

  async getMMSelectedFactoryIdsFromCookie(): Promise<string[]> {
    const selectedFactoryIdsString = await this.getCookie('selectedFactoryIds');
    return selectedFactoryIdsString ? selectedFactoryIdsString.split('~') : [];
  }
}
